import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/education-terms/EducationTermsAction";
import * as uiHelpers from "../EducationTermsUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useEducationTermsUIContext } from "../EducationTermsUIContext";

export function EducationTermsTable({
  deleteEducationTerms
}) {
  // EducationTerms UI Context
  const educationTermsUIContext = useEducationTermsUIContext();
  const educationTermsUIProps = useMemo(() => {
    return {
      ids: educationTermsUIContext.ids,
      setIds: educationTermsUIContext.setIds,
      queryParams: educationTermsUIContext.queryParams,
      setQueryParams: educationTermsUIContext.setQueryParams,
      openEditEducationTermPage: educationTermsUIContext.openEditEducationTermPage
    };
  }, [educationTermsUIContext]);

  // Getting curret state of Education Terms list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.educationTerms }),
    shallowEqual
  );
  const { semesterCount, totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Education Terms Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(actions.getEducationTerms(educationTermsUIProps.queryParams));
    return () => dispatch(actions.cleanEducationTermsState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [educationTermsUIProps.queryParams, dispatch]);

  // Table columns
  let columns = [
    {
      dataField: "term",
      text: "Eğitim Dönemi",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "firstSemesterStart",
      text: "Birinci Dönem Başlangıcı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "240px"
      }
    },
    {
      dataField: "firstSemesterEnd",
      text: "Birinci Dönem Sonu",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    }
  ];

  if (semesterCount > 1) {
    const semesterColumns = [
      {
        dataField: "secondSemesterStart",
        text: "İkinci Dönem Başlangıcı",
        sort: true,
        sortCaret: sortCaret,
        style: {
          minWidth: "240px"
        }
      },
      {
        dataField: "secondSemesterEnd",
        text: "İkinci Dönem Sonu",
        sort: true,
        sortCaret: sortCaret,
        style: {
          minWidth: "200px"
        }
      }
    ];
    columns = columns.concat(semesterColumns);
  }
  
  if (semesterCount > 2) {
    const semesterColumns = [
      {
        dataField: "thirdSemesterStart",
        text: "Üçüncü Dönem Başlangıcı",
        sort: true,
        sortCaret: sortCaret,
        style: {
          minWidth: "240px"
        }
      },
      {
        dataField: "thirdSemesterEnd",
        text: "Üçüncü Dönem Sonu",
        sort: true,
        sortCaret: sortCaret,
        style: {
          minWidth: "200px"
        }
      }
    ];
    columns = columns.concat(semesterColumns);
  }

  if (semesterCount > 3) {
    const semesterColumns = [
      {
        dataField: "fourthSemesterStart",
        text: "Dördüncü Dönem Başlangıcı",
        sort: true,
        sortCaret: sortCaret,
        style: {
          minWidth: "240px"
        }
      },
      {
        dataField: "fourthSemesterEnd",
        text: "Dördüncü Dönem Sonu",
        sort: true,
        sortCaret: sortCaret,
        style: {
          minWidth: "200px"
        }
      }
    ];
    columns = columns.concat(semesterColumns);
  }

  const statusColumn = {
    dataField: "isActive",
    text: "Aktif mi?",
    sort: true,
    sortCaret: sortCaret,
    formatter: columnFormatters.StatusColumnFormatter,
    style: {
      minWidth: "120px"
    }
  };

  columns.push(statusColumn);

  const actionColumn = {
    dataField: "action",
    text: "İşlemler",
    formatter: columnFormatters.ActionsColumnFormatter,
    formatExtraData: {
      isEditionAuthorized,
      isDeletionAuthorized,
      openEditEducationTermPage: educationTermsUIProps.openEditEducationTermPage,
      deleteEducationTerms
    },
    classes: "text-right pr-0",
    headerClasses: "text-right pr-3",
    style: {
      minWidth: "100px",
    }
  }

  if (isEditionAuthorized || isDeletionAuthorized) columns.push(actionColumn);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: educationTermsUIProps.queryParams.pageSize,
    page: educationTermsUIProps.queryParams.pageNumber
  };

  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ? entities : [] }
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                educationTermsUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: educationTermsUIProps.ids,
                setIds: educationTermsUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>
  );
}